import { Buffer } from 'buffer';

import { getImageExtensionByMimeType } from '@looko-corp/acloset-react-kit/src/utils/mimeType';
import { uuidv4 } from '@looko-corp/acloset-react-kit/src/utils/uuid';

import { createThumbnailGenerator } from '@/tools/MassiveUploaderModal/ThumbnailGenerator';
import { GeneratorParams } from '@/tools/MassiveUploaderModal/ThumbnailGenerator/types';

/**
 * Base64 데이터 파싱 유틸리티 함수
 *
 * Base64 형식의 이미지 데이터에서 미디어 타입과 실제 데이터를 분리합니다.
 *
 * @param base64OrURL Base64 형식의 데이터 또는 URL
 * @returns 파싱된 base64 문자열과 미디어 타입
 * @todo react kit에서 가져오기
 */
export function parseBase64Data(base64OrURL: string): {
  base64String: string;
  mediaType?: string;
} {
  const [mediaType, base64String] = base64OrURL.split(',');
  if (mediaType == null || base64String == null) {
    return { base64String: base64OrURL };
  }
  return { base64String, mediaType: mediaType.replace('data:', '').replace(';base64', '') };
}

/**
 * 이미지 회전 유틸리티 함수
 *
 * Canvas를 사용하여 이미지를 지정된 각도로 회전시키고 Base64 형태로 반환합니다.
 * - 90도 단위 회전 지원
 * - Canvas 2D 컨텍스트를 활용한 이미지 변환
 * - 회전 후 콜백 함수로 결과 전달
 *
 * @param srcBase64 원본 이미지 Base64 데이터
 * @param degrees 회전 각도 (도 단위)
 * @param callback 회전된 Base64를 받는 콜백 함수
 */
export function rotateImage(srcBase64: string, degrees: number, callback: (value: string) => void) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const newImage = new Image();

  newImage.crossOrigin = 'anonymous';

  const inputUrl = srcBase64;
  const outputUrl = inputUrl;
  newImage.src = outputUrl;

  // https://acloset-kr-s3.s3.amazonaws.com/shops/643e2b4ef0a876ec96965aa4_168179602134367934297.jpg
  // https://acloset-kr-s3.s3.ap-northeast-2.amazonaws.com/shops/643e2b4ef0a876ec96965aa4_168179602134367934297.jpg

  newImage.onload = function () {
    canvas.width = degrees % 180 === 0 ? newImage.width : newImage.height;
    canvas.height = degrees % 180 === 0 ? newImage.height : newImage.width;

    ctx?.translate(canvas.width / 2, canvas.height / 2);
    ctx?.rotate((degrees * Math.PI) / 180);
    ctx?.drawImage(newImage, newImage.width / -2, newImage.height / -2);

    callback(canvas.toDataURL('image/jpeg'));
  };
}

/**
 * base64 이미지를 File 타입 image 로 변환
 *
 * @deprecated base64ToImageFile2 를 사용해주세요.
 */
export function base64ToImageFile(dataURI: string, fileName: string) {
  const byteString = window.atob(dataURI.split(',')[1] ?? '');
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const int8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    int8Array[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([int8Array], { type: 'image/jpeg' });

  const file = new File([blob], fileName, { type: 'image/jpeg' });

  return file;
}

/**
 * Data URI 또는 Base64 를 File 객체로 변환합니다.
 */
export function base64ToImageFile2(dataURI: string, mimeType?: string) {
  const { base64String, mediaType = mimeType } = parseBase64Data(dataURI);

  if (mediaType == null) {
    throw new Error('Unknown mime type.');
  }

  const extension = getImageExtensionByMimeType(mediaType, true);

  const blob = new Blob([Buffer.from(base64String, 'base64')], { type: mediaType });
  return new File([blob], `${uuidv4()}.${extension}`, { type: blob.type });
}

/**
 * imageFile이나 URL을 받아서 해당 이미지 크기를 return 합니다.
 */
export async function getImageSize(image: File | string) {
  if (typeof image === 'string') {
    return new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.src = image;

      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
    });
  }

  const imageURL = window.URL.createObjectURL(image);

  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();
    img.src = imageURL;

    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
  }).finally(() => window.URL.revokeObjectURL(imageURL));
}

// ************************************************************************************************

const allowedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
export const allowedImageExtensions = new Set(['.jpg', '.jpeg', '.jfif', '.png', '.webp']);
export const allowedImageExtensionsAccept = new Array(allowedImageExtensions.values()).join(',');

/**
 * 업로드 가능한 이미지인지 판단합니다.
 *
 * @returns true 면 가능한 타입.
 */
export const isAllowedImageType = (mimeType: string) => {
  return allowedImageTypes.has(mimeType);
};

// ************************************************************************************************

/**
 * Object URL 을 파일로 바꿉니다.
 *
 * @param doRevoke object URL 을 revoke 시킵니다.
 */
export const convertObjectUrlToFile = async (
  data: {
    /**
     * Object URL
     */
    url: string;

    /**
     * mime type.
     */
    mimeType: string;

    /**
     * 확장자를 제외한 파일 이름. 없으면 UUID4 사용.
     */
    baseName?: string;
  },
  doRevoke = true,
) => {
  const newFile = new File(
    [await (await fetch(data.url)).blob()],
    `${data.baseName ?? uuidv4()}.${getImageExtensionByMimeType(data.mimeType)}`,
    {
      type: data.mimeType,
    },
  );

  if (doRevoke) {
    URL.revokeObjectURL(data.url);
  }

  return newFile;
};

// ************************************************************************************************

/**
 * 이미지 크기를 조정합니다.
 */
export const resizeImage = async (sourceImage: Blob, params: GeneratorParams, baseName?: string) => {
  const generator = createThumbnailGenerator(1);

  try {
    const result = await generator.enqueue(sourceImage, params);

    return {
      file: await convertObjectUrlToFile({ ...result, baseName }),
      width: result.width,
      height: result.height,
      mimeType: result.mimeType,
    };
  } finally {
    await generator.dispose();
  }
};
